"use client";

import { useState, useEffect } from "react";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface FundPlanItem {
  color: string;
  title: string;
  desc: string;
  percentage: number;
  angle: number;
}

interface TimelineItem {
  year: string;
  content: string;
}

const InvestmentSection = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  const fundPlan = t("INVESTMENT_SECTION_FUND_PLAN", {
    returnObjects: true,
  }) as FundPlanItem[];

  const timeline = t("INVESTMENT_SECTION_TIMELINE", {
    returnObjects: true,
  }) as TimelineItem[];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <SectionContainer
      id="investment-section"
      className="section-shell bg-black-50 text-gray-900"
    >
      <AnimatedContent className="section-inner section-stack">
        <div className="section-head">
          <h2 className="section-title">{t("INVESTMENT_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 section-lead">
            {t("INVESTMENT_SECTION_SUB_TITLE_1")} {" "}
            <strong className="text-[#207AE8]">{t("INVESTMENT_SECTION_SUB_TITLE_POINT")}</strong>
            {t("INVESTMENT_SECTION_SUB_TITLE_2")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 bg-gray-50 rounded-2xl p-8 shadow-sm text-center">
          <div>
            <p className="caption text-gray-500 mb-1">{t("INVESTMENT_SECTION_SUMMARY_1_LABEL")}</p>
            <p className="font-semibold text-[#207AE8] text-[clamp(1rem,1.8vw,1.25rem)] mb-1">
              {t("INVESTMENT_SECTION_SUMMARY_1_VALUE")}
            </p>
          </div>
          <div>
            <p className="caption text-gray-500 mb-1">{t("INVESTMENT_SECTION_SUMMARY_2_LABEL")}</p>
            <p className="font-semibold text-[#207AE8] text-[clamp(1rem,1.8vw,1.25rem)] mb-1">
              {t("INVESTMENT_SECTION_SUMMARY_2_VALUE")}
            </p>
          </div>
          <div>
            <p className="caption text-gray-500 mb-1">{t("INVESTMENT_SECTION_SUMMARY_3_LABEL")}</p>
            <p className="font-semibold text-[#207AE8] text-[clamp(1rem,1.8vw,1.25rem)] mb-1">
              {t("INVESTMENT_SECTION_SUMMARY_3_VALUE")}
            </p>
          </div>
          <div>
            <p className="caption text-gray-500 mb-1">{t("INVESTMENT_SECTION_SUMMARY_4_LABEL")}</p>
            <p className="font-semibold text-[#207AE8] text-[clamp(1rem,1.8vw,1.25rem)] mb-1">
              {t("INVESTMENT_SECTION_SUMMARY_4_VALUE")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center items-center">
            <div className="relative w-64 h-64 rounded-full flex items-center justify-center bg-[conic-gradient(#207AE8_0deg_126deg,#2C4A93_126deg_216deg,#404040_216deg_288deg,#7FD56F_288deg_324deg,#4DC274_324deg_360deg)]">
              {fundPlan.map((item) => {
                const radius = isMobile ? 96 : 120;
                const angleRad = (item.angle - 90) * (Math.PI / 180);
                const x = radius * Math.cos(angleRad);
                const y = radius * Math.sin(angleRad);

                return (
                  <div
                    key={item.title}
                    className="absolute font-bold text-white text-sm drop-shadow-md"
                    style={{
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    {item.percentage}%
                  </div>
                );
              })}

              <div className="absolute bg-white rounded-full w-32 h-32 flex items-center justify-center text-3xl font-bold text-gray-700 shadow-inner">
                100%
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="subtitle font-semibold text-gray-900 pb-4 md:pb-8">
              {t("INVESTMENT_SECTION_FUND_TITLE")}
            </h4>
            <ul className="space-y-4">
              {fundPlan.map((item) => (
                <li key={item.title} className="flex items-start gap-4">
                  <span
                    className={`w-4 h-4 rounded-full mt-1 flex-shrink-0 ${item.color}`}
                  />
                  <div>
                    <p className="text font-semibold text-gray-900 mb-1">
                      {item.title}
                    </p>
                    <p className="caption text-gray-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="p-10 space-y-8">
          <h4 className="text-[clamp(1.25rem,3vw,1.75rem)] font-semibold text-center text-gray-900 md:pb-5 pb-8">
            {t("INVESTMENT_SECTION_EFFECT_TITLE")}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {timeline.map((item) => (
              <div
                key={item.year}
                className="flex flex-col items-center text-center bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-all"
              >
                <p className="font-semibold text-[#207AE8] text-[clamp(1rem,1.8vw,1.25rem)] mb-1">
                  {item.year}
                </p>
                <p className="caption text-gray-700">{item.content}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default InvestmentSection;
